import { cn } from "@ando/ui";

import type { AccentTone } from "../catalog";

/**
 * Section header — typography-first: uppercase micro-label + count, NO marker.
 * Hierarchy comes from the type scale + section spacing (quiet 2026 look), not
 * a coloured bar. ``tone`` only tints the count chip for the connected section
 * (turquoise); every other section is neutral.
 *
 * ``loading`` mirrors the spacing so the page does NOT shift when real headers
 * arrive.
 */
export interface GroupHeaderProps {
  readonly tone: AccentTone;
  readonly label?: string;
  readonly count?: number;
  readonly loading?: boolean;
}

export const GroupHeader: React.FC<GroupHeaderProps> = ({
  tone,
  label,
  count,
  loading = false,
}) => {
  if (loading) {
    return (
      <header className="flex items-center gap-2" aria-hidden>
        <div className="h-3 w-24 animate-pulse rounded bg-ink-3/15" />
        <div className="h-4 w-6 animate-pulse rounded-full bg-ink-3/15" />
      </header>
    );
  }
  return (
    <header className="flex items-center gap-2">
      <h3 className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
        {label}
      </h3>
      {count !== undefined ? (
        <span
          className={cn(
            "rounded-full px-2 py-0.5 font-medium text-[10px] tabular-nums",
            tone === "connected" ? "bg-success/15 text-success" : "bg-ink-3/10 text-ink-3",
          )}
        >
          {count}
        </span>
      ) : null}
    </header>
  );
};
GroupHeader.displayName = "GroupHeader";
